import { apiService } from './api';

export interface PollingCallbacks {
  onNewOrders?: (orders: any[]) => void;
  onOrderUpdate?: (updates: any[]) => void;
  onStatusUpdate?: (status: any) => void;
  onNotification?: (count: number) => void;
  onError?: (error: Error) => void;
}

class PollingService {
  private pollingInterval: NodeJS.Timeout | null = null;
  private lastPollTimestamp: string | null = null;
  private callbacks: PollingCallbacks = {};
  private isPolling: boolean = false;
  private pollIntervalMs: number = 5000; // Poll every 5 seconds

  /**
   * Start polling for updates
   */
  start(callbacks: PollingCallbacks, intervalMs: number = 5000) {
    this.callbacks = callbacks;
    this.pollIntervalMs = intervalMs;
    this.isPolling = true;

    // Do first poll immediately
    this.poll();

    // Set up interval for subsequent polls
    this.pollingInterval = setInterval(() => {
      this.poll();
    }, this.pollIntervalMs);

    console.log('[Polling] Started polling with interval:', intervalMs + 'ms');
  }

  /**
   * Stop polling
   */
  stop() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
    this.isPolling = false;
    this.lastPollTimestamp = null;
    console.log('[Polling] Stopped polling');
  }

  /**
   * Perform a single poll
   */
  private async poll() {
    if (!this.isPolling) {
      return;
    }

    try {
      const response = await apiService.pollForUpdates(this.lastPollTimestamp || undefined);

      // Update last poll timestamp
      this.lastPollTimestamp = response.timestamp;

      // Handle new orders (API returns camelCase)
      if (response.newOrders && response.newOrders.length > 0) {
        console.log('[Polling] New orders received:', response.newOrders);
        if (this.callbacks.onNewOrders) {
          this.callbacks.onNewOrders(response.newOrders);
        }
      }

      // Handle order updates (API returns camelCase)
      if (response.orderUpdates && response.orderUpdates.length > 0) {
        if (this.callbacks.onOrderUpdate) {
          this.callbacks.onOrderUpdate(response.orderUpdates);
        }
      }

      // Handle marketer status updates (API returns camelCase)
      if (response.marketerStatus) {
        if (this.callbacks.onStatusUpdate) {
          this.callbacks.onStatusUpdate(response.marketerStatus);
        }
      }

      // Handle notifications (API returns camelCase)
      if (response.unreadNotifications > 0) {
        if (this.callbacks.onNotification) {
          this.callbacks.onNotification(response.unreadNotifications);
        }
      }

    } catch (error) {
      console.error('[Polling] Error during poll:', error);
      if (this.callbacks.onError) {
        this.callbacks.onError(error as Error);
      }
    }
  }

  /**
   * Force an immediate poll
   */
  async pollNow() {
    await this.poll();
  }

  /**
   * Check if currently polling
   */
  isActive(): boolean {
    return this.isPolling;
  }

  /**
   * Update polling interval
   */
  setInterval(intervalMs: number) {
    this.pollIntervalMs = intervalMs;
    if (this.isPolling) {
      // Restart with new interval
      this.stop();
      this.start(this.callbacks, intervalMs);
    }
  }
}

// Create singleton instance
const pollingService = new PollingService();

export default pollingService;
export { pollingService };
